// Definiciones LOINC, SNOMED y WE360 para observaciones clínicas
// Códigos CIE-10 y SNOMED para condiciones predefinidas

import { PARQ_QUESTIONS, FLEX_OPTIONS } from "@/pages/FormF2/helpers";
import { TIME_OPTIONS, SITTING_OPTIONS } from "@/pages/FormF3/helpers";
import {
  FOOD_ITEMS, BEHAVIOR_ITEMS, FREQ_DIET, FREQ_BEHAVIOR,
  HYDRATION_OPTIONS,
} from "@/pages/FormF4/helpers";
import {
  ANXIETY_ITEMS, ANXIETY_OPTIONS,
  STRESS_ITEMS, STRESS_OPTIONS,
  WELLBEING_ITEMS, WELLBEING_OPTIONS,
} from "@/pages/FormF5/helpers";

// ─── F1: Signos clínicos y estilos de vida ───────────────────────────

export const OBSERVATION_DEFS = {
  peso:         { code: "29463-7", display: "Peso corporal", unit: "kg", system: "http://loinc.org" },
  talla:        { code: "8302-2",  display: "Estatura", unit: "cm", system: "http://loinc.org" },
  cintura:      { code: "8280-0",  display: "Circunferencia de cintura", unit: "cm", system: "http://loinc.org" },
  paSistolica:  { code: "8480-6",  display: "Presión arterial sistólica", unit: "mmHg", system: "http://loinc.org" },
  paDiastolica: { code: "8462-4",  display: "Presión arterial diastólica", unit: "mmHg", system: "http://loinc.org" },
  glucemia:     { code: "1558-6",  display: "Glucemia en ayunas", unit: "mg/dL", system: "http://loinc.org" },
  oximetria:    { code: "2708-6",  display: "Saturación de oxígeno", unit: "%", system: "http://loinc.org" },
  tabaco:       { code: "72166-2", display: "Consumo de tabaco", unit: null, system: "http://loinc.org" },
  alcohol:      { code: "74013-4", display: "Consumo de alcohol", unit: null, system: "http://loinc.org" },
  sueno:        { code: "93832-4", display: "Horas de sueño", unit: "h", system: "http://loinc.org" },
  // Métricas cardiovasculares (FHVitals SDK)
  heartRate:             { code: "8867-4",  display: "Frecuencia cardíaca", unit: "lpm", system: "http://loinc.org" },
  heartRateVariability:  { code: "80404-7", display: "Variabilidad R-R interval", unit: "ms", system: "http://loinc.org" },
  respirationRate:       { code: "9279-1",  display: "Frecuencia respiratoria", unit: "rpm", system: "http://loinc.org" },
  stressIndex:           { code: "we360-stress-index", display: "Índice de estrés", unit: null, system: "http://we360.app" },
  // Métricas cardiovasculares avanzadas
  cardiovascularAge:     { code: "we360-cv-age", display: "Edad cardiovascular", unit: "años", system: "http://we360.app" },
  cardiovascularBMI:     { code: "we360-cv-bmi", display: "BMI cardiovascular", unit: null, system: "http://we360.app" },
  cvdRisk:               { code: "we360-cvd-risk", display: "Riesgo cardiovascular", unit: "%", system: "http://we360.app" },
  wellnessScore:         { code: "we360-wellness", display: "Puntuación de bienestar", unit: null, system: "http://we360.app" },
  ansIndexPNS:           { code: "we360-ans-pns", display: "Índice ANS Parasimpático", unit: null, system: "http://we360.app" },
  ansIndexSNS:           { code: "we360-ans-sns", display: "Índice ANS Simpático", unit: null, system: "http://we360.app" },
  // Biomarcadores clínicos de laboratorio (Gaps)
  egfr:                  { code: "62238-1", display: "Tasa de filtración glomerular estimada (eGFR)", unit: "mL/min/1.73m²", system: "http://loinc.org" },
  uacr:                  { code: "13705-9", display: "Relación albúmina/creatinina urinaria (uACR)", unit: "mg/g", system: "http://loinc.org" },
  hba1c:                 { code: "4548-4",  display: "Hemoglobina glicosilada (HbA1c)", unit: "%", system: "http://loinc.org" },
  hemoglobin:            { code: "718-7",   display: "Hemoglobina estimada", unit: "g/dL", system: "http://loinc.org" },
  cholesterol:           { code: "2093-3",  display: "Colesterol total estimado", unit: "mg/dL", system: "http://loinc.org" },
  shockIndex:            { code: "we360-shock-index", display: "Índice de shock", unit: null, system: "http://we360.app" },
  prq:                   { code: "we360-prq", display: "Cociente pulso-respiración (PRQ)", unit: null, system: "http://we360.app" },
  hrvRMSSD:              { code: "we360-hrv-rmssd", display: "HRV RMSSD", unit: "ms", system: "http://we360.app" },
  hrvSDNN:               { code: "we360-hrv-sdnn", display: "HRV SDNN", unit: "ms", system: "http://we360.app" },
  insulina:              { code: "15060-7", display: "Insulina sérica", unit: "uIU/mL", system: "http://loinc.org" },
  homair:                { code: "98380-2", display: "Índice HOMA-IR", unit: "{ratio}", system: "http://loinc.org" },
  fib4:                  { code: "94344-9", display: "Índice FIB-4", unit: "{score}", system: "http://loinc.org" },
  ast:                   { code: "1920-8",  display: "Aspartato aminotransferasa (AST)", unit: "U/L", system: "http://loinc.org" },
  alt:                   { code: "1742-6",  display: "Alanina aminotransferasa (ALT)", unit: "U/L", system: "http://loinc.org" },
  plaquetas:             { code: "777-3",   display: "Recuento de plaquetas", unit: "10^9/L", system: "http://loinc.org" },
};

const NUMERIC_KEYS = [
  "peso", "talla", "cintura", "paSistolica", "paDiastolica", "glucemia", "oximetria", "sueno", 
  "heartRate", "heartRateVariability", "respirationRate", "stressIndex", "cardiovascularAge", 
  "cardiovascularBMI", "cvdRisk", "wellnessScore", "ansIndexPNS", "ansIndexSNS",
  "hemoglobin", "cholesterol", "shockIndex", "prq", "hrvRMSSD", "hrvSDNN",
  "egfr", "uacr", "hba1c", "insulina", "homair", "fib4", "ast", "alt", "plaquetas"
];

// ─── Códigos SNOMED para respuestas categóricas ──────────────────────

export const VALUE_CODES = {
  tabaco: {
    "No":        { code: "266919005", display: "Never smoked", system: "http://snomed.info/sct" },
    "Si":        { code: "77176002",  display: "Smoker", system: "http://snomed.info/sct" },
    "Exfumador": { code: "8517006",   display: "Ex-smoker", system: "http://snomed.info/sct" },
  },
  alcohol: {
    "Nunca":              { code: "105542008", display: "Non-drinker of alcohol", system: "http://snomed.info/sct" },
    "Ocasional":          { code: "82581004",  display: "Social drinker", system: "http://snomed.info/sct" },
    "Frecuente (diario)": { code: "160573003", display: "Heavy drinker", system: "http://snomed.info/sct" },
  },
};

// ─── F2: Observaciones de evaluación física ──────────────────────────

export const F2_OBSERVATION_DEFS = {
  caminata6min: { code: "64098-7",              display: "Distancia caminata 6 minutos", unit: "m",   system: "http://loinc.org" },
  sentadillas:  { code: "we360-sentadillas-30s", display: "Sentadillas en 30 segundos",   unit: "rep", system: "http://we360.app" },
  flexiones:    { code: "we360-flexiones-max",   display: "Flexiones máximas",            unit: "rep", system: "http://we360.app" },
  flexibilidad: { code: "we360-flexibilidad",    display: "Flexibilidad (Sit & Reach)",   unit: null,  system: "http://we360.app" },
};

const F2_NUMERIC_KEYS = ["caminata6min", "sentadillas", "flexiones"];

// ─── F3: Observaciones IPAQ ──────────────────────────────────────────

export const F3_OBSERVATION_DEFS = {
  metTotal:          { code: "we360-met-total",       display: "MET-minutos/semana total",      unit: "MET-min/sem", system: "http://we360.app" },
  clasificacionIPAQ: { code: "we360-ipaq-nivel",      display: "Clasificación IPAQ",            unit: null,          system: "http://we360.app" },
  indiceSedentarismo:{ code: "we360-sedentarismo",     display: "Índice de sedentarismo",        unit: null,          system: "http://we360.app" },
};

// ═══════════════════════════════════════════════════════════════════════
// CATÁLOGOS DE CÓDIGOS ESTÁNDAR (code + display EN + displayES)
// Estos catálogos permiten procesar los datos independientemente del idioma
// ═══════════════════════════════════════════════════════════════════════

// ─── F2: Códigos PAR-Q ──────────────────────────────────────────────

const PARQ_ITEM_CODES = [
  { code: "we360:parq-1", display: "Doctor-diagnosed heart condition requiring supervised activity" },
  { code: "we360:parq-2", display: "Chest pain during physical activity" },
  { code: "we360:parq-3", display: "Chest pain at rest in the past month" },
  { code: "we360:parq-4", display: "Loss of balance or consciousness" },
  { code: "we360:parq-5", display: "Bone or joint problem worsened by activity" },
  { code: "we360:parq-6", display: "Currently prescribed BP or heart medication" },
  { code: "we360:parq-7", display: "Other reason to avoid physical activity" },
];

const FLEX_VALUE_CODES = {
  limitada: { code: "we360:flex-limited", display: "Limited flexibility", displayES: "No alcanzo los pies" },
  normal:   { code: "we360:flex-normal",  display: "Normal flexibility",  displayES: "Toco los pies" },
  superior: { code: "we360:flex-superior", display: "Superior flexibility", displayES: "Sobrepaso los pies" },
};

// ─── F3: Códigos IPAQ ───────────────────────────────────────────────

const TIME_CODE_MAP = {
  lt15:    { display: "Less than 15 minutes" },
  "15-30": { display: "15-30 minutes" },
  "30-60": { display: "30-60 minutes" },
  gt60:    { display: "More than 1 hour" },
  unsure:  { display: "Not sure" },
};

const SITTING_CODE_MAP = {
  lt2:     { display: "Less than 2 hours" },
  "2-4":   { display: "2-4 hours" },
  "4-6":   { display: "4-6 hours" },
  gt6:     { display: "More than 6 hours" },
  unsure:  { display: "Not sure" },
};

const ACTIVITY_TYPE_MAP = {
  vigorosa: { code: "we360:ipaq-vigorous",  display: "Vigorous physical activity", displayES: "Actividad física vigorosa" },
  moderada: { code: "we360:ipaq-moderate",  display: "Moderate physical activity", displayES: "Actividad física moderada" },
  caminata: { code: "we360:ipaq-walking",   display: "Walking",                    displayES: "Caminata" },
};

const IPAQ_CATEGORY_MAP = {
  high:     { display: "High (active)",  displayES: "Alto (activo)" },
  moderate: { display: "Moderate",       displayES: "Moderado" },
  low:      { display: "Low (inactive)", displayES: "Bajo (inactivo)" },
};

const SEDENTARISMO_CATEGORY_MAP = {
  low:       { display: "Low",       displayES: "Bajo" },
  moderate:  { display: "Moderate",  displayES: "Moderado" },
  high:      { display: "High",      displayES: "Alto" },
  very_high: { display: "Very high", displayES: "Muy alto" },
  unknown:   { display: "Unknown",   displayES: "Sin dato" },
};

// ─── F4: Códigos de nutrición ────────────────────────────────────────

const FOOD_CODE_MAP = {
  frutas:     { code: "we360:food-frutas",     display: "Fresh fruits" },
  verduras:   { code: "we360:food-verduras",   display: "Fresh or cooked vegetables" },
  integrales: { code: "we360:food-integrales", display: "Whole grains" },
  refinados:  { code: "we360:food-refinados",  display: "Refined products" },
  legumbres:  { code: "we360:food-legumbres",  display: "Legumes" },
  carnes:     { code: "we360:food-carnes",     display: "Lean meats or fish" },
  lacteos:    { code: "we360:food-lacteos",    display: "Dairy or plant-based alternatives" },
  grasas:     { code: "we360:food-grasas",     display: "Healthy fats" },
  frituras:   { code: "we360:food-frituras",   display: "Fried or fast food" },
  azucaradas: { code: "we360:food-azucaradas", display: "Sugary beverages" },
};

const FREQ_DIET_CODE_MAP = {
  "Nunca":              { code: "never",    display: "Never" },
  "1-2 veces/semana":   { code: "1-2pw",   display: "1-2 times/week" },
  "3-4 veces/semana":   { code: "3-4pw",   display: "3-4 times/week" },
  "Diario (1 vez)":     { code: "daily-1",  display: "Daily (once)" },
  "Diario (2+ veces)":  { code: "daily-2+", display: "Daily (2+ times)" },
};

const BEHAVIOR_CODE_MAP = {
  saltarse:      { code: "we360:behav-saltarse",      display: "Skipping main meals" },
  comida_rapida: { code: "we360:behav-comida-rapida",  display: "Ordering fast food or eating out" },
  planear:       { code: "we360:behav-planear",        display: "Meal planning ahead" },
  agua:          { code: "we360:behav-agua",           display: "Regular water intake" },
  azucar:        { code: "we360:behav-azucar",         display: "Adding sugar to beverages" },
};

const FREQ_BEHAV_CODE_MAP = {
  "Nunca":          { code: "never",       display: "Never" },
  "A veces":        { code: "sometimes",   display: "Sometimes" },
  "Frecuentemente": { code: "frequently",  display: "Frequently" },
  "Siempre":        { code: "always",      display: "Always" },
};

const DIET_CATEGORY_MAP = {
  poor:      { display: "Poor diet",      displayES: "Dieta deficiente" },
  fair:      { display: "Fair diet",      displayES: "Dieta mejorable" },
  good:      { display: "Good diet",      displayES: "Dieta buena" },
  excellent: { display: "Excellent diet", displayES: "Dieta excelente" },
};

const BEHAVIOR_CATEGORY_MAP = {
  risk:    { display: "Risk behaviors",    displayES: "Conductas de riesgo" },
  fair:    { display: "Fair behaviors",    displayES: "Conductas regulares" },
  good:    { display: "Healthy behaviors", displayES: "Conductas saludables" },
  optimal: { display: "Optimal behaviors", displayES: "Conductas óptimas" },
};

const HYDRATION_CATEGORY_MAP = {
  insufficient: { display: "Insufficient", displayES: "Insuficiente" },
  low:          { display: "Low",          displayES: "Bajo" },
  adequate:     { display: "Adequate",     displayES: "Adecuado" },
  optimal:      { display: "Optimal",      displayES: "Óptimo" },
};

const MOTIVATION_CODE_MAP = {
  "Bajar de peso":                     { code: "we360:goal-weight-loss",       display: "Weight loss" },
  "Controlar azúcar o colesterol":     { code: "we360:goal-sugar-cholesterol", display: "Sugar/cholesterol control" },
  "Mejorar energía o digestión":       { code: "we360:goal-energy-digestion",  display: "Improve energy/digestion" },
  "Ganar masa muscular":               { code: "we360:goal-muscle-gain",       display: "Muscle gain" },
  "Comer más saludable en general":    { code: "we360:goal-healthier-eating",  display: "Healthier eating overall" },
};

const MOTIVATION_LEVEL_CODE_MAP = {
  "Nada motivado/a": { code: "none",           display: "Not motivated" },
  "Poco":            { code: "low",            display: "Low motivation" },
  "Neutral":         { code: "neutral",        display: "Neutral" },
  "Motivado/a":      { code: "motivated",      display: "Motivated" },
  "Muy motivado/a":  { code: "very-motivated", display: "Highly motivated" },
};

const RESTRICTION_CODE_MAP = {
  "Vegetariano/a":                          { code: "vegetarian",         display: "Vegetarian" },
  "Vegano/a":                               { code: "vegan",             display: "Vegan" },
  "Sin gluten":                             { code: "gluten-free",       display: "Gluten-free" },
  "Sin lácteos":                            { code: "dairy-free",        display: "Dairy-free" },
  "Alergias":                               { code: "allergies",         display: "Allergies" },
  "Por religión o cultura (kosher/halal)":  { code: "religious-cultural", display: "Religious/cultural (kosher/halal)" },
  "Ninguna":                                { code: "none",              display: "None" },
  "Otra":                                   { code: "other",             display: "Other" },
};

// ─── F5: Códigos emocionales ─────────────────────────────────────────

const ANXIETY_ITEM_CODES = [
  { code: "we360:gad5-1", display: "Feeling nervous or anxious" },
  { code: "we360:gad5-2", display: "Difficulty relaxing" },
  { code: "we360:gad5-3", display: "Repetitive thoughts or constant worrying" },
  { code: "we360:gad5-4", display: "Easily irritable or upset" },
  { code: "we360:gad5-5", display: "Difficulty sleeping or staying asleep" },
];

const ANXIETY_RESPONSE_MAP = [
  { code: 0, display: "Not at all" },
  { code: 1, display: "Several days" },
  { code: 2, display: "More than half the days" },
  { code: 3, display: "Nearly every day" },
];

const STRESS_ITEM_CODES = [
  { code: "we360:stress-1", display: "Daily burden feels excessive" },
  { code: "we360:stress-2", display: "Difficulty disconnecting after work" },
  { code: "we360:stress-3", display: "Emotionally drained at end of day" },
  { code: "we360:stress-4", display: "Feeling frustrated or demotivated" },
  { code: "we360:stress-5", display: "No time for self or rest" },
];

const STRESS_RESPONSE_MAP = [
  { code: 0, display: "Never" },
  { code: 1, display: "Rarely" },
  { code: 2, display: "Sometimes" },
  { code: 3, display: "Frequently" },
  { code: 4, display: "Always" },
];

const WELLBEING_ITEM_CODES = [
  { code: "we360:wb-1", display: "Sense of purpose or value" },
  { code: "we360:wb-2", display: "Feeling emotionally supported" },
  { code: "we360:wb-3", display: "Satisfied with work-life balance" },
  { code: "we360:wb-4", display: "Moments to reconnect and rest" },
  { code: "we360:wb-5", display: "Feeling gratitude or enjoying small moments" },
];

const ANXIETY_CATEGORY_MAP = {
  minimal:  { display: "Minimal",  displayES: "Mínima" },
  mild:     { display: "Mild",     displayES: "Leve" },
  moderate: { display: "Moderate", displayES: "Moderada" },
  severe:   { display: "Severe",   displayES: "Severa" },
};

const STRESS_CATEGORY_MAP = {
  low:      { display: "Low",      displayES: "Bajo" },
  moderate: { display: "Moderate", displayES: "Moderado" },
  high:     { display: "High",     displayES: "Alto" },
  critical: { display: "Critical", displayES: "Crítico" },
};

const WELLBEING_CATEGORY_MAP = {
  low:       { display: "Low",       displayES: "Bajo" },
  moderate:  { display: "Moderate",  displayES: "Moderado" },
  high:      { display: "High",      displayES: "Alto" },
  very_high: { display: "Very high", displayES: "Muy alto" },
};

const SECTION_CODE_MAP = {
  ansiedad:  { code: "we360:gad5",    display: "Anxiety (GAD-5)",         displayES: "Ansiedad (GAD-5)" },
  estres:    { code: "we360:stress",   display: "Stress / Burnout",       displayES: "Estrés / Burnout" },
  bienestar: { code: "we360:wellbeing", display: "Subjective well-being", displayES: "Bienestar subjetivo" },
};

const GLOBAL_CATEGORY_MAP = {
  critical:  { display: "Critical",  displayES: "Crítico" },
  low:       { display: "Low",       displayES: "Bajo" },
  moderate:  { display: "Moderate",  displayES: "Moderado" },
  good:      { display: "Good",      displayES: "Bueno" },
  excellent: { display: "Excellent", displayES: "Excelente" },
};

// ─── Condiciones predefinidas (F1) ───────────────────────────────────

export const PREDEFINED_CONDITIONS = [
  { id: "pa_elevada",    nombre: "PA elevada (120-129/<80)",       codigoCIE10: "R03.0",  codigoSNOMED: "24184005" },
  { id: "hta1",          nombre: "HTA Estadio I (≥130/80)",       codigoCIE10: "I10",    codigoSNOMED: "38341003" },
  { id: "hta2",          nombre: "HTA Estadio II (≥140/90)",      codigoCIE10: "I10",    codigoSNOMED: "38341003" },
  { id: "hiperglicemia", nombre: "Hiperglicemia (100-125 mg/dl)", codigoCIE10: "R73.09", codigoSNOMED: "80394007" },
  { id: "diabetes",      nombre: "Diabetes (≥126 mg/dl)",         codigoCIE10: "E11.9",  codigoSNOMED: "73211009" },
  { id: "dislipidemias", nombre: "Dislipidemias",                 codigoCIE10: "E78.5",  codigoSNOMED: "370992007" },
  { id: "sobrepeso",     nombre: "Sobrepeso (IMC 25-29)",         codigoCIE10: "E66.3",  codigoSNOMED: "238131007" },
  { id: "obesidad",      nombre: "Obesidad (IMC ≥30)",            codigoCIE10: "E66.9",  codigoSNOMED: "414916001" },
];

// ─── Helpers genéricos ───────────────────────────────────────────────

export function getSigno(signos, code) {
  return signos?.find((s) => s.code === code)?.value ?? null;
}

export function getSignoStr(signos, code) {
  const v = getSigno(signos, code);
  return v != null ? String(v) : "";
}

export function getPredefinedCondition(id) {
  return PREDEFINED_CONDITIONS.find((c) => c.id === id) || null;
}

// ─── Helpers F1: signos (clínicos + estilo de vida) ──────────────────

export function buildSignosFromForm(data) {
  const signos = [];
  for (const [key, def] of Object.entries(OBSERVATION_DEFS)) {
    const raw = data[key];
    if (raw === "" || raw == null) continue;
    const isNumeric = NUMERIC_KEYS.includes(key);
    const value = isNumeric ? parseFloat(raw) : raw;
    if (isNumeric && isNaN(value)) continue;

    const obs = {
      code: def.code,
      display: def.display,
      value,
      unit: def.unit,
      system: def.system,
    };

    const vcMap = VALUE_CODES[key];
    if (vcMap && vcMap[raw]) {
      obs.valueCode = vcMap[raw];
    }

    signos.push(obs);
  }
  return signos;
}

export function formFieldsFromSignos(signos) {
  const fields = {};
  for (const [key, def] of Object.entries(OBSERVATION_DEFS)) {
    const v = getSigno(signos, def.code);
    fields[key] = v != null ? String(v) : "";
  }
  return fields;
}

// ─── Helpers F1: condiciones ─────────────────────────────────────────

export function buildCondicionesArray(condicionIds, autoConditionIds, condicionesAdicionales) {
  const allIds = [...new Set([...(condicionIds || []), ...(autoConditionIds || [])])];
  const predefined = allIds.map((id) => {
    const def = getPredefinedCondition(id);
    if (!def) return null;
    return {
      nombre: def.nombre,
      codigoCIE10: def.codigoCIE10,
      codigoSNOMED: def.codigoSNOMED,
      source: autoConditionIds.includes(id) ? "auto-detected" : "manual",
    };
  }).filter(Boolean);
  return [...predefined, ...(condicionesAdicionales || [])];
}

export function decomposeCondiciones(condiciones) {
  const condicionIds = [];
  const condicionesAdicionales = [];
  for (const c of (condiciones || [])) {
    const match = PREDEFINED_CONDITIONS.find(
      (p) => p.nombre === c.nombre || (p.codigoCIE10 === c.codigoCIE10 && p.codigoSNOMED === c.codigoSNOMED)
    );
    if (match) {
      condicionIds.push(match.id);
    } else {
      condicionesAdicionales.push(c);
    }
  }
  return { condicionIds, condicionesAdicionales };
}

// ─── Helpers F2: observaciones físicas ───────────────────────────────

export function buildF2Firestore(mem) {
  if (!mem) return mem;
  if (Array.isArray(mem.observaciones) || Array.isArray(mem.observations)) return mem;
  const {
    caminata6min, sentadillas, flexiones, flexibilidad,
    clasificacion6mwt, clasificacionSentadillas, clasificacionFlexiones, clasificacionFlexibilidad,
    ...rest
  } = mem;

  const observaciones = [];
  for (const [key, def] of Object.entries(F2_OBSERVATION_DEFS)) {
    const raw = mem[key];
    if (raw === "" || raw == null) continue;
    const isNumeric = F2_NUMERIC_KEYS.includes(key);
    const value = isNumeric ? Number(raw) : raw;
    if (isNumeric && isNaN(value)) continue;
    const obs = {
      code: def.code,
      display: def.display,
      value,
      unit: def.unit,
      system: def.system,
    };
    // Flexibilidad: add coded value
    if (key === "flexibilidad") {
      const fc = FLEX_VALUE_CODES[raw];
      if (fc) {
        obs.valueCode = { code: fc.code, display: fc.display, displayES: fc.displayES };
      }
    }
    observaciones.push(obs);
  }

  // Enrich parqResponses with coded questions
  const enrichedParq = Array.isArray(rest.parqResponses)
    ? rest.parqResponses.map((val, idx) => ({
        code: PARQ_ITEM_CODES[idx]?.code || `we360:parq-${idx + 1}`,
        display: PARQ_ITEM_CODES[idx]?.display || `PAR-Q item ${idx + 1}`,
        displayES: PARQ_QUESTIONS[idx] || `Pregunta ${idx + 1}`,
        value: typeof val === "object" && val !== null ? (val.value ?? val.respuesta) : val,
      }))
    : rest.parqResponses;

  return {
    ...rest,
    parqResponses: enrichedParq,
    instrumento: "PAR-Q+",
    observaciones,
    clasificacion6mwt: clasificacion6mwt || null,
    clasificacionSentadillas: clasificacionSentadillas || null,
    clasificacionFlexiones: clasificacionFlexiones || null,
    clasificacionFlexibilidad: clasificacionFlexibilidad || null,
  };
}

export function f2ToMem(fs) {
  if (!fs || !fs.observaciones) return fs;
  const { observaciones, instrumento, ...rest } = fs;
  const flat = {};
  for (const [key, def] of Object.entries(F2_OBSERVATION_DEFS)) {
    const v = getSigno(observaciones, def.code);
    flat[key] = v != null ? (F2_NUMERIC_KEYS.includes(key) ? v : v) : (F2_NUMERIC_KEYS.includes(key) ? 0 : "");
  }
  // Unpack enriched parqResponses to simple booleans
  if (Array.isArray(rest.parqResponses)) {
    rest.parqResponses = rest.parqResponses.map((item) => {
      if (typeof item === "object" && item !== null) {
        if ("value" in item) return item.value;
        if ("respuesta" in item) return item.respuesta;
      }
      return item;
    });
  }
  return { ...rest, ...flat };
}

// ─── Helpers F3: observaciones IPAQ ──────────────────────────────────

function enrichTimeCode(code) {
  if (!code) return code;
  const opt = TIME_OPTIONS.find((o) => o.value === code);
  const en = TIME_CODE_MAP[code];
  return opt ? { code, display: en?.display || code, displayES: opt.label } : code;
}

function enrichSittingCode(code) {
  if (!code) return code;
  const opt = SITTING_OPTIONS.find((o) => o.value === code);
  const en = SITTING_CODE_MAP[code];
  return opt ? { code, display: en?.display || code, displayES: opt.label } : code;
}

export function buildF3Firestore(mem) {
  if (!mem) return mem;
  if (Array.isArray(mem.observaciones) || Array.isArray(mem.observations)) return mem;
  const { metTotal, clasificacionIPAQ, indiceSedentarismo, ...rest } = mem;

  const observaciones = [];

  if (metTotal != null && !isNaN(metTotal)) {
    const def = F3_OBSERVATION_DEFS.metTotal;
    observaciones.push({ code: def.code, display: def.display, value: metTotal, unit: def.unit, system: def.system });
  }
  if (clasificacionIPAQ) {
    const def = F3_OBSERVATION_DEFS.clasificacionIPAQ;
    const cat = IPAQ_CATEGORY_MAP[clasificacionIPAQ];
    observaciones.push({
      code: def.code, display: def.display,
      value: clasificacionIPAQ,
      valueDisplay: cat?.display || clasificacionIPAQ,
      valueDisplayES: cat?.displayES || clasificacionIPAQ,
      unit: def.unit, system: def.system,
    });
  }
  if (indiceSedentarismo) {
    const def = F3_OBSERVATION_DEFS.indiceSedentarismo;
    const cat = SEDENTARISMO_CATEGORY_MAP[indiceSedentarismo];
    observaciones.push({
      code: def.code, display: def.display,
      value: indiceSedentarismo,
      valueDisplay: cat?.display || indiceSedentarismo,
      valueDisplayES: cat?.displayES || indiceSedentarismo,
      unit: def.unit, system: def.system,
    });
  }

  // Enrich activity types with codes + time codes
  const enriched = { ...rest };
  for (const [actKey, actDef] of Object.entries(ACTIVITY_TYPE_MAP)) {
    if (enriched[actKey]) {
      enriched[actKey] = {
        code: actDef.code,
        display: actDef.display,
        displayES: actDef.displayES,
        dias: enriched[actKey].dias,
        tiempo: enrichTimeCode(enriched[actKey].tiempo),
        metMin: enriched[actKey].metMin,
      };
    }
  }
  if (enriched.tiempoSentado) {
    enriched.tiempoSentado = enrichSittingCode(enriched.tiempoSentado);
  }

  return { ...enriched, instrumento: "IPAQ-SF", observaciones };
}

export function f3ToMem(fs) {
  if (!fs || !fs.observaciones) return fs;
  const { observaciones, instrumento, ...rest } = fs;
  const unpacked = { ...rest };
  for (const key of ["vigorosa", "moderada", "caminata"]) {
    if (unpacked[key]) {
      const { code: _c, display: _d, displayES: _dES, ...actData } = unpacked[key];
      if (actData.tiempo && typeof actData.tiempo === "object") {
        actData.tiempo = actData.tiempo.code || actData.tiempo.valor;
      }
      unpacked[key] = actData;
    }
  }
  if (typeof unpacked.tiempoSentado === "object" && unpacked.tiempoSentado !== null) {
    unpacked.tiempoSentado = unpacked.tiempoSentado.code || unpacked.tiempoSentado.valor;
  }
  return {
    ...unpacked,
    metTotal: getSigno(observaciones, "we360-met-total") ?? 0,
    clasificacionIPAQ: getSigno(observaciones, "we360-ipaq-nivel") ?? null,
    indiceSedentarismo: getSigno(observaciones, "we360-sedentarismo") ?? null,
  };
}

// ─── Helpers F4: nutrición ───────────────────────────────────────────

function enrichFreqDiet(freqES) {
  const fc = FREQ_DIET_CODE_MAP[freqES];
  return fc ? { code: fc.code, display: fc.display, displayES: freqES } : freqES;
}

function enrichFreqBehav(freqES) {
  const fc = FREQ_BEHAV_CODE_MAP[freqES];
  return fc ? { code: fc.code, display: fc.display, displayES: freqES } : freqES;
}

export function buildF4Firestore(mem) {
  if (!mem) return mem;
  if (mem.consumo?.[0]?.code || mem.foodIntake?.[0]?.code) return mem;
  const result = { ...mem };

  // Enrich consumo items with codes
  if (Array.isArray(result.consumo)) {
    result.consumo = result.consumo.map((item, idx) => {
      const foodDef = FOOD_ITEMS[idx];
      const fc = foodDef ? FOOD_CODE_MAP[foodDef.id] : null;
      return {
        code: fc?.code || `we360:food-item-${idx + 1}`,
        display: fc?.display || item.alimento,
        displayES: item.alimento,
        frecuencia: enrichFreqDiet(item.frecuencia),
        score: item.score,
      };
    });
  }

  // Enrich categories
  if (result.categoriaCalidadDieta) {
    const cat = DIET_CATEGORY_MAP[result.categoriaCalidadDieta];
    result.categoriaCalidadDieta = {
      key: result.categoriaCalidadDieta,
      display: cat?.display || result.categoriaCalidadDieta,
      displayES: cat?.displayES || result.categoriaCalidadDieta,
    };
  }

  // Enrich conductas items with codes
  if (Array.isArray(result.conductas)) {
    result.conductas = result.conductas.map((item, idx) => {
      const behavDef = BEHAVIOR_ITEMS[idx];
      const bc = behavDef ? BEHAVIOR_CODE_MAP[behavDef.id] : null;
      return {
        code: bc?.code || `we360:behav-item-${idx + 1}`,
        display: bc?.display || item.pregunta,
        displayES: item.pregunta,
        frecuencia: enrichFreqBehav(item.frecuencia),
        score: item.score,
      };
    });
  }

  if (result.categoriaConductas) {
    const cat = BEHAVIOR_CATEGORY_MAP[result.categoriaConductas];
    result.categoriaConductas = {
      key: result.categoriaConductas,
      display: cat?.display || result.categoriaConductas,
      displayES: cat?.displayES || result.categoriaConductas,
    };
  }

  // Enrich hidratacion
  if (result.hidratacion && typeof result.hidratacion === "string") {
    const hydOpt = HYDRATION_OPTIONS.find((o) => o.key === result.hidratacion);
    const hydCat = HYDRATION_CATEGORY_MAP[result.hidratacion];
    if (hydOpt) {
      result.hidratacion = {
        key: result.hidratacion,
        display: hydCat?.display || result.hidratacion,
        displayES: hydCat?.displayES || result.hidratacion,
        descripcion: hydOpt.label,
      };
    }
  }

  // Enrich motivaciones
  if (Array.isArray(result.motivaciones)) {
    result.motivaciones = result.motivaciones.map((text) => {
      const mc = MOTIVATION_CODE_MAP[text];
      return mc ? { code: mc.code, display: mc.display, displayES: text } : text;
    });
  }

  // Enrich nivelMotivacion
  if (result.nivelMotivacion && typeof result.nivelMotivacion === "string") {
    const ml = MOTIVATION_LEVEL_CODE_MAP[result.nivelMotivacion];
    if (ml) {
      result.nivelMotivacion = { code: ml.code, display: ml.display, displayES: result.nivelMotivacion };
    }
  }

  // Enrich restricciones
  if (Array.isArray(result.restricciones)) {
    result.restricciones = result.restricciones.map((text) => {
      const rc = RESTRICTION_CODE_MAP[text];
      return rc ? { code: rc.code, display: rc.display, displayES: text } : text;
    });
  }

  return result;
}

export function f4ToMem(fs) {
  if (!fs) return fs;
  const result = { ...fs };

  // Unpack consumo
  if (Array.isArray(result.consumo) && result.consumo[0]?.code) {
    result.consumo = result.consumo.map((item) => ({
      alimento: item.displayES || item.display,
      frecuencia: typeof item.frecuencia === "object" ? (item.frecuencia.displayES || item.frecuencia.display) : item.frecuencia,
      score: item.score,
    }));
  }

  // Unpack categories
  if (typeof result.categoriaCalidadDieta === "object" && result.categoriaCalidadDieta?.key) {
    result.categoriaCalidadDieta = result.categoriaCalidadDieta.key;
  }

  // Unpack conductas
  if (Array.isArray(result.conductas) && result.conductas[0]?.code) {
    result.conductas = result.conductas.map((item) => ({
      pregunta: item.displayES || item.display,
      frecuencia: typeof item.frecuencia === "object" ? (item.frecuencia.displayES || item.frecuencia.display) : item.frecuencia,
      score: item.score,
    }));
  }

  if (typeof result.categoriaConductas === "object" && result.categoriaConductas?.key) {
    result.categoriaConductas = result.categoriaConductas.key;
  }

  if (typeof result.hidratacion === "object" && result.hidratacion?.key) {
    result.hidratacion = result.hidratacion.key;
  }

  // Unpack motivaciones
  if (Array.isArray(result.motivaciones) && typeof result.motivaciones[0] === "object") {
    result.motivaciones = result.motivaciones.map((item) =>
      typeof item === "object" ? (item.displayES || item.display) : item
    );
  }

  // Unpack nivelMotivacion
  if (typeof result.nivelMotivacion === "object" && result.nivelMotivacion?.displayES) {
    result.nivelMotivacion = result.nivelMotivacion.displayES;
  }

  // Unpack restricciones
  if (Array.isArray(result.restricciones) && typeof result.restricciones[0] === "object") {
    result.restricciones = result.restricciones.map((item) =>
      typeof item === "object" ? (item.displayES || item.display) : item
    );
  }

  return result;
}

// ─── Helpers F5: salud emocional ─────────────────────────────────────

function enrichF5Section(section, sectionMeta, itemCodes, responseMap, itemsES, optionsES, categoryMap) {
  if (!section?.respuestas) return section;

  const enrichedResponses = section.respuestas.map((val, idx) => {
    const itemCode = itemCodes[idx];
    const resp = responseMap[val];
    return {
      code: itemCode?.code || `we360:item-${idx + 1}`,
      display: itemCode?.display || `Item ${idx + 1}`,
      displayES: itemsES[idx]?.label || `Item ${idx + 1}`,
      value: val,
      valueDisplay: resp?.display || String(val),
      valueDisplayES: optionsES[val] || String(val),
    };
  });

  const cat = section.categoria ? categoryMap[section.categoria] : null;
  const enrichedCategoria = section.categoria
    ? { key: section.categoria, display: cat?.display || section.categoria, displayES: cat?.displayES || section.categoria }
    : section.categoria;

  return {
    code: sectionMeta.code,
    display: sectionMeta.display,
    displayES: sectionMeta.displayES,
    respuestas: enrichedResponses,
    score: section.score,
    categoria: enrichedCategoria,
  };
}

export function buildF5Firestore(mem) {
  if (!mem) return mem;
  if (
    typeof mem.ansiedad?.respuestas?.[0] === "object" ||
    typeof mem.anxiety?.responses?.[0] === "object"
  ) {
    return mem;
  }
  const result = { ...mem };

  result.ansiedad = enrichF5Section(
    result.ansiedad, SECTION_CODE_MAP.ansiedad, ANXIETY_ITEM_CODES, ANXIETY_RESPONSE_MAP, ANXIETY_ITEMS, ANXIETY_OPTIONS, ANXIETY_CATEGORY_MAP
  );
  result.estres = enrichF5Section(
    result.estres, SECTION_CODE_MAP.estres, STRESS_ITEM_CODES, STRESS_RESPONSE_MAP, STRESS_ITEMS, STRESS_OPTIONS, STRESS_CATEGORY_MAP
  );
  result.bienestar = enrichF5Section(
    result.bienestar, SECTION_CODE_MAP.bienestar, WELLBEING_ITEM_CODES, STRESS_RESPONSE_MAP, WELLBEING_ITEMS, WELLBEING_OPTIONS, WELLBEING_CATEGORY_MAP
  );

  if (result.categoriaGlobal) {
    const cat = GLOBAL_CATEGORY_MAP[result.categoriaGlobal];
    result.categoriaGlobal = {
      key: result.categoriaGlobal,
      display: cat?.display || result.categoriaGlobal,
      displayES: cat?.displayES || result.categoriaGlobal,
    };
  }

  return result;
}

export function f5ToMem(fs) {
  if (!fs) return fs;
  const result = { ...fs };

  for (const section of ["ansiedad", "estres", "bienestar"]) {
    if (result[section]) {
      // Strip section-level codes
      const { code: _c, display: _d, displayES: _dES, ...sectionData } = result[section];

      // Unpack enriched respuestas to plain indices
      if (sectionData.respuestas?.[0] && typeof sectionData.respuestas[0] === "object") {
        sectionData.respuestas = sectionData.respuestas.map((item) => {
          if ("value" in item) return item.value;
          if ("valor" in item) return item.valor;
          return item;
        });
      }
      if (typeof sectionData.categoria === "object" && sectionData.categoria?.key) {
        sectionData.categoria = sectionData.categoria.key;
      }
      result[section] = sectionData;
    }
  }

  if (typeof result.categoriaGlobal === "object" && result.categoriaGlobal?.key) {
    result.categoriaGlobal = result.categoriaGlobal.key;
  }

  return result;
}


// ═══════════════════════════════════════════════════════════════════════
// KEY TRANSLATION: Spanish in-memory keys ↔ English payload keys
// The React UI uses flat Spanish keys internally. These maps translate
// them to standard English keys at the Firestore/API boundary.
// ═══════════════════════════════════════════════════════════════════════

function _tKeys(obj, map) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const r = {};
  for (const [k, v] of Object.entries(obj)) r[map[k] || k] = v;
  return r;
}

function _inv(map) {
  const r = {};
  for (const [k, v] of Object.entries(map)) r[v] = k;
  return r;
}

export const KEY_MAPS = {
  profile: {
    nombres: "firstName",
    apellidos: "lastName",
    fechaNacimiento: "birthDate",
    sexo: "sex",
    estadoCivil: "maritalStatus",
    nivelEducativo: "educationLevel",
    profesion: "profession",
    ocupacion: "occupation",
    residencia: "residence",
    usaMedicamentos: "usesMedication",
    medicamentos: "medications",
    consentimiento: "consent",
    signos: "vitalSigns",
    condiciones: "conditions",
  },
  medication: {
    nombre: "name",
    codigoATC: "atcCode",
    dosis: "dosage",
    nombreComercial: "brandName",
  },
  condition: {
    nombre: "name",
    codigoCIE10: "icd10Code",
    codigoSNOMED: "snomedCode",
  },
  f2: {
    parqAlerta: "parqAlert",
    instrumento: "instrument",
    observaciones: "observations",
    clasificacion6mwt: "classification6mwt",
    clasificacionSentadillas: "classificationSquats",
    clasificacionFlexiones: "classificationPushups",
    clasificacionFlexibilidad: "classificationFlexibility",
    confirmacion: "confirmation",
  },
  f3: {
    vigorosa: "vigorous",
    moderada: "moderate",
    caminata: "walking",
    tiempoSentado: "sittingTime",
    instrumento: "instrument",
    observaciones: "observations",
    clasificacionIPAQ: "classificationIPAQ",
    indiceSedentarismo: "sedentaryIndex",
  },
  f3Activity: {
    dias: "days",
    tiempo: "time",
    metMin: "metMinutes",
  },
  f4: {
    consumo: "foodIntake",
    scoreCalidadDieta: "dietQualityScore",
    categoriaCalidadDieta: "dietQualityCategory",
    conductas: "behaviors",
    scoreConductas: "behaviorScore",
    categoriaConductas: "behaviorCategory",
    hidratacion: "hydration",
    motivaciones: "motivations",
    nivelMotivacion: "motivationLevel",
    restricciones: "restrictions",
    restriccionOtra: "otherRestriction",
    alergias: "allergies",
  },
  f4Item: {
    frecuencia: "frequency",
    descripcion: "description",
  },
  f5: {
    ansiedad: "anxiety",
    estres: "stress",
    bienestar: "wellbeing",
    scoreGlobal: "globalScore",
    categoriaGlobal: "globalCategory",
    comentario1: "comment1",
    comentario2: "comment2",
  },
  f5Section: {
    respuestas: "responses",
    categoria: "category",
  },
};

// Pre-computed reverse maps (English → Spanish)
const _REV = Object.fromEntries(
  Object.entries(KEY_MAPS).map(([k, v]) => [k, _inv(v)])
);

/** Translate an individual form's data to English keys */
export function translateFormKeys(form, data) {
  if (!data) return data;
  switch (form) {
    case "F1": {
      let r = { ...data };
      if (Array.isArray(r.medicamentos))
        r.medicamentos = r.medicamentos.map(m => _tKeys(m, KEY_MAPS.medication));
      if (Array.isArray(r.condiciones))
        r.condiciones = r.condiciones.map(c => _tKeys(c, KEY_MAPS.condition));
      return _tKeys(r, KEY_MAPS.profile);
    }
    case "F2":
      return _tKeys(data, KEY_MAPS.f2);
    case "F3": {
      let r = { ...data };
      for (const k of ["vigorosa", "moderada", "caminata"])
        if (r[k] && typeof r[k] === "object")
          r[k] = _tKeys(r[k], KEY_MAPS.f3Activity);
      return _tKeys(r, KEY_MAPS.f3);
    }
    case "F4": {
      let r = { ...data };
      if (Array.isArray(r.consumo))
        r.consumo = r.consumo.map(i => _tKeys(i, KEY_MAPS.f4Item));
      if (Array.isArray(r.conductas))
        r.conductas = r.conductas.map(i => _tKeys(i, KEY_MAPS.f4Item));
      if (r.hidratacion && typeof r.hidratacion === "object")
        r.hidratacion = _tKeys(r.hidratacion, KEY_MAPS.f4Item);
      return _tKeys(r, KEY_MAPS.f4);
    }
    case "F5": {
      let r = { ...data };
      for (const k of ["ansiedad", "estres", "bienestar"])
        if (r[k] && typeof r[k] === "object")
          r[k] = _tKeys(r[k], KEY_MAPS.f5Section);
      return _tKeys(r, KEY_MAPS.f5);
    }
    default:
      return data;
  }
}

/** Translate a full Firestore profile payload to English keys */
export function payloadToEnglish(data) {
  if (!data) return data;
  let r = { ...data };
  if (Array.isArray(r.medicamentos))
    r.medicamentos = r.medicamentos.map(m => _tKeys(m, KEY_MAPS.medication));
  if (Array.isArray(r.condiciones))
    r.condiciones = r.condiciones.map(c => _tKeys(c, KEY_MAPS.condition));
  if (r.f2Results) r.f2Results = translateFormKeys("F2", r.f2Results);
  if (r.f3Results) r.f3Results = translateFormKeys("F3", r.f3Results);
  if (r.f4Results) r.f4Results = translateFormKeys("F4", r.f4Results);
  if (r.f5Results) r.f5Results = translateFormKeys("F5", r.f5Results);
  return _tKeys(r, KEY_MAPS.profile);
}

/** Reverse: English-keyed payload back to Spanish for in-memory use (backward compatible) */
export function payloadToSpanish(data) {
  if (!data) return data;
  let r = _tKeys(data, _REV.profile);

  if (Array.isArray(r.medicamentos))
    r.medicamentos = r.medicamentos.map(m => _tKeys(m, _REV.medication));
  if (Array.isArray(r.condiciones))
    r.condiciones = r.condiciones.map(c => _tKeys(c, _REV.condition));

  if (r.f2Results)
    r.f2Results = _tKeys(r.f2Results, _REV.f2);

  if (r.f3Results) {
    let f3 = _tKeys(r.f3Results, _REV.f3);
    for (const k of ["vigorosa", "moderada", "caminata"])
      if (f3[k] && typeof f3[k] === "object")
        f3[k] = _tKeys(f3[k], _REV.f3Activity);
    r.f3Results = f3;
  }

  if (r.f4Results) {
    let f4 = _tKeys(r.f4Results, _REV.f4);
    if (Array.isArray(f4.consumo))
      f4.consumo = f4.consumo.map(i => _tKeys(i, _REV.f4Item));
    if (Array.isArray(f4.conductas))
      f4.conductas = f4.conductas.map(i => _tKeys(i, _REV.f4Item));
    if (f4.hidratacion && typeof f4.hidratacion === "object")
      f4.hidratacion = _tKeys(f4.hidratacion, _REV.f4Item);
    r.f4Results = f4;
  }

  if (r.f5Results) {
    let f5 = _tKeys(r.f5Results, _REV.f5);
    for (const k of ["ansiedad", "estres", "bienestar"])
      if (f5[k] && typeof f5[k] === "object")
        f5[k] = _tKeys(f5[k], _REV.f5Section);
    r.f5Results = f5;
  }

  return r;
}
